import { routeLinkMapQuote, routePageMapQuote, routeServiceNameMapQuote, objTealium } from 'utils';

export class TealiumService {
  static landingPageName = 'vodalend compare: landing page';
  static quoteLoanPageName = 'vodalend compare: quick quote: loan details';
  static quotePersonalPageName = 'vodalend compare: quick quote: personal details';
  static quoteEmploymentPageName = 'vodalend compare: quick quote: employment details';
  static quoteOfferPageName = 'vodalend compare: quick quote: view offers';
  static offerSuggestionPageName = 'vodalend compare: experience feedback';
  static quoteOfferThankYouPageName = 'vodalend compare: quick quote: thank you';

  static attempt = (func) => {
    try {
      func();
    } catch (err) {
      console.error(err);
    }
  };

  static sendViewToTealium(obj) {
    this.attempt(() => {
      objTealium.view(obj);
    });
  }

  static sendLinkToTealium(obj) {
    this.attempt(() => {
      objTealium.link(obj);
    });
  }

  static onBack(route) {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      service_application_type: 'transaction',
      page_name: routePageMapQuote[route],
      service_application_name: routeServiceNameMapQuote[route],
      link_name: routeLinkMapQuote[route],
    });
  }

  /**
   *
   * Form Events
   *
   */
  static onFormError(errorMessage, route) {
    const page_name = routePageMapQuote[route];
    this.sendLinkToTealium({
      page_form_event: 'error',
      page_name,
      page_form_name: page_name,
      page_errors: errorMessage,
      page_error_type: 'user error',
      page_error_code: '-1',
    });
  }

  static onFormInteraction(route) {
    const page_name = routePageMapQuote[route];
    this.sendLinkToTealium({
      page_form_event: 'started',
      page_name,
      page_form_name: page_name,
    });
  }

  /**
   *
   * Page name: landing page
   * Page url: /compare/get-started
   *
   * */
  static onLandingStart() {
    this.sendViewToTealium({
      event_name: 'view',
      page_name: this.landingPageName,
      service_application_name: 'vodalend compare: landing page',
      service_application_type: 'transaction',
      service_application_event: 'start',
    });
  }

  static onQuote() {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      page_name: this.landingPageName,
      service_application_name: 'vodalend compare: landing page',
      service_application_type: 'transaction',
      service_application_event: 'finish',
      link_name: 'vodalend compare: landing page: quick quote',
    });
  }

  /**
   *
   * Page name: request
   * Page url: /compare/loan
   *
   * */
  static onLoanDetailStart() {
    this.sendViewToTealium({
      event_name: 'view',
      service_application_type: 'transaction',
      service_application_event: 'start',
      page_name: this.quoteLoanPageName,
      service_application_name: 'vodalend compare: quick quote: loan details',
    });
  }

  static onLoanDetailEnd(amount, termAmount, loanUsage) {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      service_application_type: 'transaction',
      service_application_event: 'finish',
      loan_amount: amount,
      term_amount: termAmount,
      load_usage: loanUsage,
      page_name: this.quoteLoanPageName,
      service_application_name: 'vodalend compare: quick quote: loan details',
      link_name: 'vodalend compare: quick quote: loan details: continue',
    });
  }

  /**
   *
   * Page name: personal-details
   * Page url: /compare/personal-details
   *
   * */
  static onPersonalDetailsStart() {
    this.sendViewToTealium({
      event_name: 'view',
      service_application_type: 'transaction',
      service_application_event: 'start',
      page_name: this.quotePersonalPageName,
      service_application_name: 'vodalend compare: quick quote: personal details',
    });
  }

  static onPersonalDetailsCheckbox() {
    this.sendLinkToTealium({
      event_name: 'link',
      page_tool_event: 'use',
      page_name: this.quotePersonalPageName,
      page_tool_name: 'vodalend compare: quick quote: personal details: consent',
    });
  }

  static onPersonalDetailsEnd() {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      service_application_type: 'transaction',
      service_application_event: 'finish',
      page_name: this.quotePersonalPageName,
      service_application_name: 'vodalend compare: quick quote: personal details',
      link_name: 'vodalend compare: quick quote: personal details: continue',
      page_form_name: 'vodalend compare: quick quote: personal details',
    });
  }

  /**
   *
   * Page name: offers
   * Page url: /compare/offers
   *
   * */
  static onQuotesStart(noQuotes) {
    this.sendViewToTealium({
      event_name: 'view',
      service_application_event: 'start',
      service_application_type: 'transaction',
      number_of_offers: noQuotes,
      page_name: this.quoteOfferPageName,
      service_application_name: 'vodalend compare: quick quote: product select',
    });
  }

  static onQuotesEnd(productOption) {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      service_application_type: 'transaction',
      service_application_event: 'finish',
      product_option: productOption,
      page_name: this.quoteOfferPageName,
      page_event: 'vodalend_quote',
      service_application_name: 'vodalend compare: quick quote: product select',
      link_name: 'vodalend compare: quick quote: product select: continue',
    });
  }

  static onOfferRedirect(companyName) {
    this.sendViewToTealium({
      event_name: 'view',
      page_name: `vodalend compare: quick quote: ${companyName} redirect: success`,
    });
  }

  /**
   *
   * Page name: suggestions
   * Page url: /compare/offers/suggestions
   *
   * */
  static onOfferSuggestionStart() {
    this.sendViewToTealium({
      event_name: 'view',
      page_name: this.offerSuggestionPageName,
      service_application_event: 'start',
      service_application_name: "vodalend compare: not what you're looking for",
      service_application_type: 'query',
    });
  }

  static onOfferSuggestionExit() {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      page_name: this.offerSuggestionPageName,
      link_name: 'vodalend compare:experience feedback: exit',
    });
  }

  static onOfferSuggestionSubmit(optionSelected) {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      page_name: this.offerSuggestionPageName,
      link_name: `vodalend compare: experience feedback: ${optionSelected}`,
      service_application_event: 'finish',
      service_application_name: "vodalend compare: not what you're looking for",
      service_application_type: 'query',
    });
  }

  /**
   *
   * Page name: loan glossary
   * Page url: /compare/offers
   *
   * */
  static onLoanGlossary() {
    this.sendViewToTealium({
      event_name: 'view',
      page_name: 'vodalend compare: quick quote: product select: loan glossary',
      service_application_name: 'vodalend compare: quick quote: product select: loan glossary',
      service_application_type: 'transaction',
    });
  }

  /**
   *
   * Page name: thank you
   * Page url: /compare/offers/thank-you
   *
   * */
  static onThankYouStart() {
    this.sendViewToTealium({
      page_name: this.quoteOfferThankYouPageName,
      page_event: 'view, vodalend_quote',
      service_application_name: 'vodalend compare: quick quote: thank you',
    });
  }

  /**
   *
   * Page name: offer-failed
   * Page url: /compare/offers/unsuccessful
   *
   * */
  static onApplicationFailedStart() {
    this.sendViewToTealium({
      event_name: 'view',
      page_name: 'vodalend compare: application unsuccessful',
      service_application_name: 'vodalend compare: application unsuccessful',
      service_application_type: 'transaction',
      service_application_event: 'start',
    });
  }

  static onApplicationFailedExit() {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      page_name: 'vodalend compare: application unsuccessful',
      link_name: 'vodalend compare: application unsuccessful: exit',
      service_application_name: 'vodalend compare: application unsuccessful',
      service_application_type: 'transaction',
      service_application_event: 'finish',
    });
  }

  static onApplicationFailedReportIssue() {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      page_name: 'vodalend compare: application unsuccessful',
      link_name: 'vodalend compare: application unsuccessful: report an issue',
      service_application_name: 'vodalend compare: application unsuccessful',
      service_application_type: 'transaction',
      service_application_event: 'finish',
    });
  }

  /**
   *
   * Page name: technical-error
   *
   * */
  static onTechnicalErrorStart() {
    this.sendViewToTealium({
      event_name: 'view',
      page_name: 'vodalend compare: technical error',
      service_application_name: 'vodalend compare: technical error',
      service_application_type: 'transaction',
      service_application_event: 'start',
    });
  }

  static onTechnicalTryAgain() {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      page_name: 'vodalend compare: technical error',
      link_name: 'vodalend compare: technical error: exit',
      service_application_name: 'vodalend compare: technical error',
      service_application_type: 'transaction',
      service_application_event: 'finish',
    });
  }

  static onTechnicalErrorExit() {
    this.sendLinkToTealium({
      event_name: 'link',
      link_type: 'o',
      page_name: 'vodalend compare: technical error',
      link_name: 'vodalend compare: technical error: try again',
      service_application_name: 'vodalend compare: technical error',
      service_application_type: 'transaction',
      service_application_event: 'finish',
    });
  }
}
